import api, { ListResponse } from "../api";

export enum BucketNumberEnum {
  BUCKET_1 = 1,
  BUCKET_2 = 2,
  BUCKET_3 = 3,
}

export interface BucketAsset {
  id: number;
  bucket: number;
  asset: number;
  symbol: string;
  asset__name: string;
  is_main: boolean;
  included: boolean;
  // Live backend check: does the pair's AccountHistory (PORTFOLIO chain)
  // exist? Reports need it.
  has_history: boolean;
  // Why the last history auto-generation attempt failed; null after success.
  history_error: string | null;
}

export interface BucketHistoryFailure {
  symbol: string;
  error: string;
}

// Bulk member-adding endpoints also auto-generate account histories and
// report which pairs failed. Adds bigger than the backend's inline limit
// defer generation instead (history_pending) — the batched Generate
// histories flow / nightly sync picks those up.
export interface AddedAssetsResponse {
  added: number;
  history_failed: BucketHistoryFailure[];
  history_pending: number;
}

// One batch of history generation. The caller loops, passing
// next_after_id back as afterId, until remaining hits 0 — failed members
// keep their stored error but the cursor moves past them.
export interface EnsureHistoryResponse {
  processed: number;
  failed: BucketHistoryFailure[];
  remaining: number;
  next_after_id: number | null;
}

export interface Bucket {
  id: number;
  account: number;
  account__number: string;
  number: BucketNumberEnum;
  assets: BucketAsset[];
}

export interface BucketListParams {
  account?: number;
  account__client?: number;
}

// Create returns a light payload (no nested members — a populated Bucket 3
// can hold ~900 of them); `added`/`history_pending` describe the Bucket 3
// auto-population.
export interface CreateBucketResponse {
  id: number;
  account: number;
  number: BucketNumberEnum;
  added: number;
  history_pending: number;
}

const LIST_TAG = { type: "Buckets", id: "LIST" } as const;
// Member mutations touch a single bucket — invalidate just it, so only the
// cached lists that actually contain that bucket refetch.
const bucketTag = (_result: unknown, _error: unknown, arg: { bucketId: number }) => [
  { type: "Buckets", id: arg.bucketId } as const,
];

const bucketAndHistoryTags = (
  _result: unknown,
  _error: unknown,
  arg: { bucketId: number },
) => [
  { type: "Buckets", id: arg.bucketId } as const,
  "AccountHistory" as const,
];

export const apiWithBuckets = api
  .enhanceEndpoints({
    addTagTypes: ["Buckets", "AccountHistory"],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      listBuckets: builder.query<ListResponse<Bucket>, BucketListParams>({
        query: (params) => ({
          url: `/trading/buckets/`,
          params,
          method: "GET",
        }),
        providesTags: (result) => [
          LIST_TAG,
          ...(result?.results ?? []).map(
            ({ id }) => ({ type: "Buckets", id }) as const,
          ),
        ],
      }),
      // Light response on purpose — no nested members. A freshly populated
      // Bucket 3 can hold ~900 assets; the full bucket arrives via the
      // LIST refetch this mutation triggers.
      createBucket: builder.mutation<
        CreateBucketResponse,
        { account: number; number: BucketNumberEnum }
      >({
        query: (body) => ({
          url: `/trading/buckets/`,
          method: "POST",
          body,
        }),
        invalidatesTags: [LIST_TAG],
      }),
      deleteBucket: builder.mutation<void, { id: number }>({
        query: ({ id }) => ({
          url: `/trading/buckets/${id}/`,
          method: "DELETE",
        }),
        invalidatesTags: [LIST_TAG],
      }),
      addBucketAsset: builder.mutation<
        // warning: set when the member was added but its AccountHistory
        // could not be generated.
        BucketAsset & { warning?: string },
        { bucketId: number; assetId: number }
      >({
        query: ({ bucketId, assetId }) => ({
          url: `/trading/buckets/${bucketId}/assets/`,
          method: "POST",
          body: { asset_id: assetId },
        }),
        invalidatesTags: bucketTag,
      }),
      // Buckets 1/2: deletes the member row. Bucket 3: flips it to excluded.
      removeBucketAsset: builder.mutation<
        BucketAsset | void,
        { bucketId: number; assetId: number }
      >({
        query: ({ bucketId, assetId }) => ({
          url: `/trading/buckets/${bucketId}/assets/${assetId}/`,
          method: "DELETE",
        }),
        invalidatesTags: bucketTag,
      }),
      updateBucketAsset: builder.mutation<
        BucketAsset,
        {
          bucketId: number;
          assetId: number;
          is_main?: boolean;
          included?: boolean;
        }
      >({
        query: ({ bucketId, assetId, ...patch }) => ({
          url: `/trading/buckets/${bucketId}/assets/${assetId}/`,
          method: "PATCH",
          body: patch,
        }),
        // Optimistic: flip the flag in every cached bucket list immediately,
        // roll back on failure. No tag invalidation — the server returns the
        // same state.
        async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
          const patches = apiWithBuckets.util
            .selectInvalidatedBy(getState(), [
              { type: "Buckets", id: arg.bucketId },
            ])
            .filter((entry) => entry.endpointName === "listBuckets")
            .map(({ originalArgs }) =>
              dispatch(
                apiWithBuckets.util.updateQueryData(
                  "listBuckets",
                  originalArgs,
                  (draft) => {
                    const bucket = draft.results.find(
                      (b) => b.id === arg.bucketId,
                    );
                    const member = bucket?.assets.find(
                      (a) => a.asset === arg.assetId,
                    );
                    if (!bucket || !member) return;
                    if (arg.is_main !== undefined) {
                      member.is_main = arg.is_main;
                    }
                    if (arg.included !== undefined) {
                      member.included = arg.included;
                    }
                  },
                ),
              ),
            );
          try {
            await queryFulfilled;
          } catch {
            patches.forEach((patch) => patch.undo());
          }
        },
      }),
      syncBucket: builder.mutation<AddedAssetsResponse, { bucketId: number }>({
        query: ({ bucketId }) => ({
          url: `/trading/buckets/${bucketId}/sync/`,
          method: "POST",
        }),
        invalidatesTags: bucketTag,
      }),
      // Bucket 2: add every stock the account currently holds (same
      // discovery as Bucket 3's auto-population).
      addHeldAssets: builder.mutation<
        AddedAssetsResponse,
        { bucketId: number }
      >({
        query: ({ bucketId }) => ({
          url: `/trading/buckets/${bucketId}/add_held_assets/`,
          method: "POST",
        }),
        invalidatesTags: bucketTag,
      }),
      // One BATCH of AccountHistory generation for members that still lack
      // it (or whose last attempt failed) — loop with afterId to cover a
      // large Bucket 3.
      ensureBucketHistory: builder.mutation<
        EnsureHistoryResponse,
        { bucketId: number; afterId?: number }
      >({
        query: ({ bucketId, afterId }) => ({
          url: `/trading/buckets/${bucketId}/ensure_history/`,
          method: "POST",
          body: afterId ? { after_id: afterId } : {},
        }),
        invalidatesTags: bucketAndHistoryTags,
      }),
    }),
  });

export const {
  useListBucketsQuery,
  useCreateBucketMutation,
  useDeleteBucketMutation,
  useAddBucketAssetMutation,
  useRemoveBucketAssetMutation,
  useUpdateBucketAssetMutation,
  useSyncBucketMutation,
  useAddHeldAssetsMutation,
  useEnsureBucketHistoryMutation,
} = apiWithBuckets;
